<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcDashboardChart',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    dashboardId: {
      type: null,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    height: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const url = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
  });
  const b_2248226175642056_style = computed(() => ({
    height: props.height || '600px',
    overflow: 'hidden',
  }));
  const b_1889980785424776_style = computed(() => ({
    minHeight: 0,
  }));

  const $data = computed(() => ({
    url: url.value,
    ux: ux.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
    b_1889980785424776_style: b_1889980785424776_style.value,
  }));

  const $$load_url = () => {
    return $fn.value
      .fetch('get_superset_dashboard_token', {
        parameters: { id: props.dashboardId },
      })
      .then(({ url }) => url);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'url' },
      { name: 'ux' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
      { name: 'b_1889980785424776_style', nodeId: '1889980785424776' },
    ],
    loaders: { url: $$load_url },
    ux: { loadingPlaceholder: 'none' },
    dataRefs: { url, ux, b_2248226175642056_style, b_1889980785424776_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.dashboardId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dashboardId');
    }
  );
  watch(
    () => props.title,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('title');
    }
  );
  watch(
    () => props.height,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('height');
    }
  );

  const act_7315092382398562_reloadComponentData_9e8op34xv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ikqs2g6f9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_9e8op34xv,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ikqs2g6f9],
    event: 'reload',
  });
  const act_6705964765214521_reloadComponentData_ylcf4hkn2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6705964765214521_click_dl45omeqh =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_reloadComponentData_ylcf4hkn2,
      event: 'click',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_dl45omeqh],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_ikqs2g6f9,
    inst_evh_7315092382398562_reload,
    inst_evh_6705964765214521_click_dl45omeqh,
    inst_evh_6705964765214521_click,
  };

  const evh_7315092382398562_reload_ikqs2g6f9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ikqs2g6f9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click_dl45omeqh = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_dl45omeqh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card" :style="b_2248226175642056_style">
      <div class="card-header d-flex align-items-center">
        <span class="fw-semibold">
          {{ title || gettext('Dashboard') }}
        </span>
        <div class="ms-auto"></div>
        <ItpButton
          icon="fa6-solid:arrows-rotate"
          :tooltip="gettext('Reload')"
          :spinning="reloading"
          size="sm"
          variant="link"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
      </div>
      <ItpSkeleton fill v-if="!loaded"> </ItpSkeleton>
      <div
        class="card-body p-0 flex-grow-1"
        v-if="url"
        :style="b_1889980785424776_style"
      >
        <ItpKeepAliveIframe
          kind="url"
          placement="inline"
          :cacheKey="'dashboard-' + dashboardId"
          :src="url"
          class="w-100 h-100"
        >
        </ItpKeepAliveIframe>
      </div>
      <div class="card-body text-muted flex-grow-1" v-if="loaded && !url">
        {{ gettext('This dashboard could not be loaded.') }}
      </div>
    </div>
  </ItpUsercontrol>
</template>
